import React, { useEffect, useMemo, useState, useCallback, memo } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import Select from 'react-select';
import ReactTable from 'react-table';
import withFixedColumns from 'react-table-hoc-fixed-columns';
import swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { toast } from 'react-toastify';
import debounce from 'lodash/debounce';
import ls from 'localstorage-slim';
import axios from '../../../../../axios-instance';
import NewLoader from '../../../../../components/NewLoader/NewLoader';
import { getHeaders } from '../../../../../helpers/getHeaders';
import { ManagementAction } from './ManagementAction.js';
import { loadFile } from '../../../../../helpers/loadFile';
import PleaseWaitModal from '../../../../BarCodes/modals/PleaseWaitModal';
import { IconButton } from '../../../../../components/UI/IconButton/IconButton';
import Icon from '../../../../../components/UI/Icon/Icon.js';
import { ActionTicketModal } from './Modals/ActionTicketModal.js';

import '../EventTransactions/EventTransactions.sass';
import './Management.sass';
import { TabelSettings } from '../../../../../components/TabelSettings/TabelSettings.jsx';
import { createPortal } from 'react-dom';
import { LostTicketModal } from './Modals/LostTicketModal.jsx';
import { ChangeNameModal } from './Modals/ChangeNameModal.jsx';
import { ChangeSeatModal } from './Modals/ChangeSeatModal.jsx';

const MySwal = withReactContent(swal);

export const statuses = [
  { value: 1, label: 'Active' },
  { value: 2, label: 'Cancelled' },
  { value: 3, label: 'Refunded' },
  { value: 4, label: 'Resold' },
  { value: 5, label: 'Lost' },
];

function arePropsEqual(oldProps, newProps) {
  return (
    oldProps.data === newProps.data &&
    oldProps.pageSize === newProps.pageSize &&
    oldProps.currentPageNumber === newProps.currentPageNumber &&
    oldProps.columns === newProps.columns
  );
}

const Table = memo(
  ({
    columns,
    currentPageNumber,
    pageSize,
    data,
    totalItems,
    handleCurrentPageChanged,
    handlePageSizeChanged,
  }) => {
    const ReactTableFixedColumns = withFixedColumns(ReactTable);

    return (
      <ReactTableFixedColumns
        data={data}
        columns={columns}
        manual
        showPagination={true}
        pageSizeOptions={[100, 200, 500, 1000, 100000]}
        minRows={0}
        page={currentPageNumber}
        pageSize={pageSize}
        pages={Math.ceil(totalItems / pageSize)}
        onPageChange={handleCurrentPageChanged}
        onPageSizeChange={handlePageSizeChanged}
        sortable={false}
        getTrProps={() => ({
          style: {
            alignItems: 'stretch',
          },
        })}
      />
    );
  },
  arePropsEqual
);

const EventManagment = (params) => {
  const token = params.token;
  const initInputParams = {
    query: '',
    area: '',
    side: '',
    row: '',
    seat: '',
  };

  const [data, setData] = useState([]);
  const [ticketTemplates, setTicketTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedTicketTemplate, setSelectedTicketTemplate] = useState(null);
  const [inputParams, setInputParams] = useState(initInputParams);
  const [isRequest, setRequest] = useState(false);
  const [isMinimized, setMinimized] = useState(true);
  const [columnsList, setColumnsList] = useState([]);
  const [checkedColumns, setCheckedColumns] = useState([]);

  const [currentPageNumber, setCurrentPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [isClearButtonShow, setClearButtonShow] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [scrollThumbWidth, setScrollThumbWidth] = useState(0);

  const [openedPopup, setOpenedPopup] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const request = async (asCSV = false) => {
    return axios
      .post(
        '/ManageTickets/List',
        {
          searchQuery: inputParams.query,
          eventId: params.match.params.id,
          status: selectedStatus ? selectedStatus.value : null,
          ticketTemplateId: selectedTicketTemplate
            ? selectedTicketTemplate.value
            : null,
          area: inputParams.area,
          side: inputParams.side,
          row: inputParams.row,
          seat: inputParams.seat,
          pageSize,
          page: currentPageNumber,
          asCSV,
        },
        {
          headers: getHeaders(token),
        }
      )
      .catch((e) => {
        toast.error('Internal Server Error');
      });
  };

  const getData = async () => {
    setLoading(true);

    try {
      const { data } = await request();

      setData(data.list);
      setTotalItems(data.total);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeStatus = (item) => {
    setSelectedStatus(item);
    setRequest(true);
    setCurrentPageNumber(0);
  };
  const handleChangeTemplate = (item) => {
    setSelectedTicketTemplate(item);
    setRequest(true);
    setCurrentPageNumber(0);
  };

  const handleDebonceFn = () => {
    setRequest(true);
  };

  const debounceFn = useCallback(debounce(handleDebonceFn, 1500), []);

  const handleChangeInputParams = (key, value) => {
    setCurrentPageNumber(0);
    setInputParams((prev) => {
      return {
        ...prev,
        [key]: value,
      };
    });

    debounceFn();
  };

  const clearSeat = () => {
    setCurrentPageNumber(0);
    setInputParams((prev) => ({
      ...initInputParams,
      query: prev.query,
    }));
    setRequest(true);
    setClearButtonShow(false);
  };

  const handleCurrentPageChanged = (value) => {
    setCurrentPageNumber(value);
    setRequest(true);
  };

  const handlePageSizeChanged = (value) => {
    setPageSize(value);
    setCurrentPageNumber(0);
    setRequest(true);
  };

  const handleOpenPopup = (
    ticket,
    popupName,
    customClass = 'confirm-lost-ticket-modal'
  ) => {
    setSelectedTicket(ticket);

    MySwal.fire({
      onOpen: () => {
        setOpenedPopup(popupName);
      },
      onClose: closePopup,
      showConfirmButton: false,
      customClass,
    });
  }

  const closePopup = () => {
    MySwal.close();
    setOpenedPopup(null);
    setSelectedTicket(null);
  };

  const lostTicket = async (ticketId, userToken) => {
    setLoading(true);
    closePopup();

    return axios
      .post(`/ManageTickets/Lost/` + ticketId, null, {
        headers: getHeaders(userToken),
      })
      .then((response) => {
        if (response.status === 200) {
          toast.success('Ticket successfully regenerated');
          getData();

          return;
        }

        throw new Error(response.data.errorMessage);
      })
      .catch((err) => {
        toast.error('Ticket not marked as loat and barcode not regenerated!');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const changeName = async (ticket, fullName) => {
    if (fullName) {
      setLoading(true);
      closePopup();

      axios
        .put(
          '/ManageTickets',
          { ticketId: ticket.id, fullName },
          { headers: getHeaders(token) }
        )
        .then((response) => {
          if (response.status === 200) {
            if (response.data) {
              toast.success('Name successfully changed');

              getData();

              return;
            } else {
              throw new Error(response.data.errorMessage);
            }
          }
        })
        .catch((err) => {
          toast.error('Name not changed');
        })
        .finally(() => setLoading(false));
    }
  }

  const changeSeat = async (ticket, complexSeat) => {
    if (complexSeat) {
      let [seat, row, side, area] = complexSeat.split('-').reverse();

      if (!area) {
        area = side;
        side = undefined;
      }

      area = !!area ? area.toUpperCase() : area;
      side = !!side ? side.toUpperCase() : side;
      row = !!row ? row.toUpperCase() : row;
      seat = !!seat ? seat.toUpperCase() : seat;

      const changeSeatRequest = async (isAllowSwap = false) => {
        setLoading(true);
        closePopup();

        return axios
          .put(
            '/ManageTickets/ChangeSeat',
            {
              ticketId: ticket.id,
              area: area,
              side: side,
              row: row,
              seat: seat,
              allowSwap: isAllowSwap,
            },
            { headers: getHeaders(token) }
          )
          .catch((err) => {
            toast.error('Seat not changed');
          })
          .finally(() => setLoading(false));
      };

      changeSeatRequest()
        .then((response) => {
          if (response.data.isSuccess) {
            toast.success('Seat successfully changed');

            getData();

            return;
          } else if (response.data.errorMessage === 'Seat is occupied') {
            MySwal.fire({
              title: response.data.errorMessage,
              text: 'Do you want to swap tickets with another seat?',
              showCancelButton: true,
              cancelButtonText: 'Cancel',
              confirmButtonText: 'Swap',
              reverseButtons: true,
              customClass: 'confirm-cancel-offer-modal',
            }).then((result) => {
              if (result.value) {
                changeSeatRequest(true).then(() => {
                  toast.success('Seat successfully changed');
                  getData();

                  return;
                });
              }
            });
          } else {
            toast.error('Seat not changed');

            throw new Error(response.data.errorMessage);
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }

  const downloadDataAsFile = (data, name) => {
    if (data) {
      loadFile(data, name);

      toast.success('File downloaded successfully');
    } else {
      toast.error('Failed to retrieve data');
    }
  };

  const downloadReportHandler = async () => {
    try {
      try {
        MySwal.fire({
          html: <PleaseWaitModal message='Downloading file…' />,
          customClass: 'please-wait-modal',
        });

        const { data } = await request(true);

        downloadDataAsFile(data, `Tickets Report.csv`);
      } finally {
        MySwal.close();
      }
    } catch (e) {
      toast.error('File was not downloaded');
      console.log(e);
    }
  };

  const toggleMinimized = () => {
    setMinimized((prev) => !prev);
  };

  useEffect(() => {
    if (params.currentEvent.id) {
      const templates = params.currentEvent.ticketTemplates;
      const ticketTemplatesForSelect = templates.map((t) => ({
        value: t.id,
        label: t.ticketName,
      }));
      setTicketTemplates(ticketTemplatesForSelect);
    }

    setTimeout(() => {
      const list = document.getElementsByClassName('rt-table');

      if (list && list.length > 0) {
        const table = list[0];

        setScrollThumbWidth(table.offsetWidth - table.clientWidth);
      }
    }, 0);
  }, [data]);

  useEffect(() => {
    if (
      inputParams.area ||
      inputParams.row ||
      inputParams.seat ||
      inputParams.side
    ) {
      setClearButtonShow(true);
    } else {
      setClearButtonShow(false);
    }

    if (isRequest) {
      getData();
      setRequest(false);
    }
  }, [
    selectedStatus,
    selectedTicketTemplate,
    isRequest,
    inputParams,
    pageSize,
    currentPageNumber,
  ]);

  useEffect(() => {
    if (params.currentEvent.id) {
      getData();
    }
  }, [params.currentEvent.id]);

  const getLabelStyle = () => ({
    style: {
      marginTop: '6px',
    },
  });

  const getEmailStyle = () => ({
    style: {
      marginTop: '3px',
    },
  });

  const getFlexStyle = () => ({
    style: {
      padding: '3px 5px 0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: 'auto',
    },
  });

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      height: '46px',
      'min-height': '46px',
      cursor: 'pointer',
    }),
    placeholder: (base) => ({
      ...base,
      color: '#ABABAB',
    }),
  };

  /*if (!data.length && !loading) {
    return <Page404 />;
  }*/

  const columns = [
    {
      Header: 'Ticket Type',
      id: 'Ticket Type',
      accessor: 'ticketName',
      width: 220,
      resizable: false,
      getProps: getLabelStyle,
      Cell: ({
        row: {
          _original: { ticketName },
        },
      }) => <span title={ticketName}>{ticketName}</span>,
    },
    {
      Header: 'Seat',
      id: 'Seat',
      resizable: false,
      width: 120,
      getProps: getLabelStyle,
      Cell: ({
        row: {
          _original: { area, side, row, seat },
        },
      }) => {
        if (area && side && row && seat) {
          return `${area}-${side}-${row}-${seat}`;
        }
        return '';
      },
      getLabelStyle,
    },
    {
      Header: 'Barcode',
      id: 'Barcode',
      accessor: 'barcode',
      resizable: false,
      width: 260,
      getProps: getLabelStyle,
      Cell: ({
        row: {
          _original: { barcode },
        },
      }) => <span title={barcode}>{barcode}</span>,
    },
    {
      Header: 'Full Name',
      id: 'Full Name',
      accessor: 'fullName',
      resizable: false,
      width: 220,
      getProps: getLabelStyle,
      Cell: ({
        row: {
          _original: { fullName },
        },
      }) => <span title={fullName}>{fullName}</span>,
    },
    {
      Header: 'Email',
      id: 'Email',
      accessor: 'customerEmail',
      resizable: false,
      width: 260,
      getProps: getEmailStyle,
      Cell: ({
        row: {
          _original: { customerEmail },
        },
      }) => <span title={customerEmail}>{customerEmail}</span>,
    },
    {
      Header: 'Status',
      id: 'Status',
      resizable: false,
      width: 120,
      Cell: ({
        row: {
          _original: { status },
        },
      }) => {
        const currStatus = statuses.find((s) => s.value === status);
        if (currStatus)
          return (
            <div className={`status-label status-label--${status}`}>
              {currStatus.label}
            </div>
          );
        return '';
      },
    },
    {
      Header: 'Scans count',
      id: 'Scans count',
      resizable: false,
      width: 110,
      Cell: ({
        row: {
          _original: { scansCount },
        },
      }) => {
        return <span>{scansCount > 0 ? scansCount : 'Not scanned'}</span>;
      },
      getProps: getLabelStyle,
    },
    {
      Cell: ({ row: { _original: data } }) => {
        return (
          <ManagementAction
            ticket={data}
            showRefundTicketModal={(ticket) => handleOpenPopup(ticket, 'refund', 'refund-tickets-modal')}
            cancelTicketHandler={(ticket) => handleOpenPopup(ticket, 'cancel', 'refund-tickets-modal')}
            changeNameHandler={(ticket) => handleOpenPopup(ticket, 'change-name')}
            lostTicketHandler={(ticket) => handleOpenPopup(ticket, 'lost')}
            changeSeatHandler={(ticket) => handleOpenPopup(ticket, 'change-seat')}
          />
        );
      },
      width: 80,
      getProps: getFlexStyle,
      fixed: 'right',
    },
  ];

  const ALL = 'Select all';

  const checkboxesList = [ALL].concat(
    columns.filter((c) => c.id).map((c) => c.id)
  );

  const toggleColumn = (col) => {
    let newCheckedColumns = [];

    if (col === ALL) {
      if (checkedColumns.includes(col)) {
        newCheckedColumns = checkboxesList.slice(1, 2);
      } else {
        newCheckedColumns = [...checkboxesList];
      }
    } else if (checkedColumns.includes(col)) {
      newCheckedColumns = checkedColumns.filter((c) => c !== col && c !== ALL);
    } else {
      const colArray = [...checkedColumns, col];
      if (colArray.length === checkboxesList.length - 1) {
        newCheckedColumns = [...checkboxesList];
      } else {
        newCheckedColumns = columns
          .filter((c) => colArray.includes(c.id))
          .map((c) => c.id);
      }
    }

    setCheckedColumns(newCheckedColumns);
    ls.set('manageColumns', newCheckedColumns);
  };

  useEffect(() => {
    const selectedColumns = columns.filter((c) =>
      checkedColumns.includes(c.id)
    );
    setColumnsList([...selectedColumns, columns.at(-1)]);
  }, [checkedColumns]);

  useEffect(() => {
    if (ls.get('manageColumns')) {
      setCheckedColumns(ls.get('manageColumns'));
    } else {
      setCheckedColumns(checkboxesList);
    }
  }, []);

  return (
    <div className='transactions management'>
      <div className='management__searchPanels'>
        <div className='management__searchPanel searchPanel'>
          <Select
            isClearable
            options={statuses}
            placeholder='Select status'
            name='searchProfileStatus'
            classNamePrefix='custom-select'
            value={selectedStatus}
            onChange={handleChangeStatus}
            className='seasonpass__select searchPanel__select'
            styles={customSelectStyles}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: '#6071B5',
                primary25: '#F2F4FE',
                primary50: '#F2F4FE',
                primary75: '#F2F4FE',
              },
            })}
          />
          <input
            className='searchPanel__search input-behavior search-icon'
            type='text'
            placeholder='Search'
            value={inputParams.query}
            onChange={(e) => handleChangeInputParams('query', e.target.value)}
          />

          <button
            className={`btn-primary searchPanel__btn_minimized ${
              !isMinimized ? 'minimized' : ''
            }`}
            onClick={toggleMinimized}
          >
            <Icon name={'chevron'} />
          </button>

          <button className='btn-primary' onClick={downloadReportHandler}>
            Download Report
          </button>
        </div>

        {!isMinimized && (
          <div className='management__searchPanel'>
            <Select
              isClearable
              options={ticketTemplates}
              placeholder='Ticket type'
              name='searchProfileStatus'
              classNamePrefix='custom-select'
              value={selectedTicketTemplate}
              onChange={handleChangeTemplate}
              className='seasonpass__select searchPanel__select'
              styles={customSelectStyles}
              theme={(theme) => ({
                ...theme,
                colors: {
                  ...theme.colors,
                  primary: '#6071B5',
                  primary25: '#F2F4FE',
                  primary50: '#F2F4FE',
                  primary75: '#F2F4FE',
                },
              })}
            />
            <input
              className='searchPanel__search-small input-behavior'
              type='text'
              placeholder='Area'
              value={inputParams.area}
              onChange={(e) => handleChangeInputParams('area', e.target.value)}
            />
            <input
              className='searchPanel__search-small input-behavior'
              type='text'
              placeholder='Side'
              value={inputParams.side}
              onChange={(e) =>
                handleChangeInputParams('side', e.target.value.toUpperCase())
              }
            />
            <input
              className='searchPanel__search-small input-behavior'
              type='text'
              placeholder='Row'
              value={inputParams.row}
              onChange={(e) =>
                handleChangeInputParams('row', e.target.value.toUpperCase())
              }
            />
            <input
              className='searchPanel__search-small input-behavior'
              type='text'
              placeholder='Seat'
              value={inputParams.seat}
              onChange={(e) => handleChangeInputParams('seat', e.target.value)}
            />

            {isClearButtonShow && (
              <div className='searchPanel__clear-wrapper' title='Clear Seat'>
                <IconButton
                  iconName={'clear'}
                  className='searchPanel__clear'
                  onClick={clearSeat}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className='management__loader'>
          <NewLoader />
        </div>
      ) : data.length ? (
        <div
          className='management__table-wrapper'
          style={{
            height: `${
              isMinimized ? 'calc(100vh - 235px)' : 'calc(100vh - 300px)'
            }`,
          }}
        >
          <div
            className='management__table-settings-wrapper'
            style={{
              right: 25 + scrollThumbWidth + 'px',
            }}
          >
            <TabelSettings
              checkboxesList={checkboxesList}
              checkedColumns={checkedColumns}
              toggleColumn={toggleColumn}
            />
          </div>
          <Table
            columns={columnsList}
            currentPageNumber={currentPageNumber}
            pageSize={pageSize}
            data={data}
            totalItems={totalItems}
            handleCurrentPageChanged={handleCurrentPageChanged}
            handlePageSizeChanged={handlePageSizeChanged}
            isMinimized={isMinimized}
          />
        </div>
      ) : (
        <div className='events__empty-state'>
          <img src='/images/img-empty-events.svg' alt='Empty membership' />
        </div>
      )}

      {openedPopup === 'refund' &&
        selectedTicket &&
        createPortal(
          <ActionTicketModal
            ticket={selectedTicket}
            token={token}
            actionWordList={['refund', 'refunded']}
            endpoint={'/ManageTickets/Refund'}
            getData={getData}
            setLoading={setLoading}
            closeModalHandler={closePopup}
          />,
          MySwal.getContent()
        )}

      {openedPopup === 'cancel' &&
        selectedTicket &&
        createPortal(
          <ActionTicketModal
            ticket={selectedTicket}
            token={token}
            actionWordList={['cancel', 'cancelled']}
            endpoint={'/ManageTickets/CancelTickets'}
            getData={getData}
            setLoading={setLoading}
            closeModalHandler={closePopup}
          />,
          MySwal.getContent()
        )}

      {openedPopup === 'lost' &&
        selectedTicket &&
        createPortal(
        <LostTicketModal
          closeModalHandler={closePopup}
          confirmHandler={() => lostTicket(selectedTicket.id, token)}
        />,
        MySwal.getContent())}

      {openedPopup === 'change-name' &&
        selectedTicket &&
        createPortal(
        <ChangeNameModal
          ticket={selectedTicket}
          closeModalHandler={closePopup}
          changeName={changeName}
        />,
        MySwal.getContent())}

      {openedPopup === 'change-seat' &&
        selectedTicket &&
        createPortal(
        <ChangeSeatModal
          ticket={selectedTicket}
          closeModalHandler={closePopup}
          changeSeat={changeSeat}
        />,
        MySwal.getContent())}
    </div>
  );
};

const mapStateToProps = ({ auth, events: { currentEvent } }) => ({
  auth,
  token: auth.user.token,
  currentEvent,
});

const mapDispatchToProps = (dispatch) => ({});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(EventManagment)
);
